import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, FormControl, FormHelperText, Stack, Typography } from '@mui/material';
import { Card, CardContent, CardHeader, FormLabel } from './CampaignWizardStyles';
import { useQuery } from '@tanstack/react-query';

import { CampaignWizardStepProps } from './CampaignWizard';
import { AudienceSource } from '../../../../types/AudienceSource';
import { DspAudience } from '../../../../types/DspAudience';
import { toCampaignAdGroupAudienceSources, toDspAudienceDTO } from '../../../../hooks/useAudienceSources';
import { useCampaignWizardContext } from '../../../../hooks/useCampaignWizard';
import ApiService from '../../../../ApiService';
import AudienceSourceField from '../../../../components/Audience/AudienceSourceField';
import CampaignAudienceTypeRadioGroup from '../CampaignAudienceTypeRadioGroup';
import Column from '../../../../components/Column';
import Row from '../../../../components/Row';

const defaultTargetAudienceIds = [
    '418042870179667348',
    '400690001636931136',
    '428078254701556779',
    '371466814155648275',
    '410334997859980072',
    '387582609423143493',
    '367454088916754363',
    '381776062289894133',
    '376664705684416936',
    '400230741649012650',
    '378075018889757615',
    '408243089917349549',
    '386306087666729446',
    '412812846697216883',
    '425043177702158897',
    '388767989456796718',
    '380817284209253346',
    '399902627195615527',
    '372402063791512503',
    '400379810603076489',
    '385465523562591870',
    '393406455482887264',
    '400829950299131072',
    '425799680259879622',
];

type CampaignTargetingAudienceCardProps = {} & CampaignWizardStepProps;

export default function CampaignTargetingAudienceCard(props: CampaignTargetingAudienceCardProps) {
    const { campaign, onChange } = props;

    const $campaignWizard = useCampaignWizardContext();
    const { hasError, isDraftCampaign, isNewCampaign } = $campaignWizard;

    const { $audienceSources, $loader } = $campaignWizard;
    const {
        audienceSources: selections = [],
        fetchAudienceSources: fetchSelections = () => {},
        isFetchingAudienceSources: isFetchingSelections = false,
    } = $audienceSources || {};

    const [audienceSources, setAudienceSources] = useState<AudienceSource[]>([]);

    const adGroupAudienceSources = useMemo(() => {
        return campaign.adGroups?.[0]?.adGroupAudienceSources ?? [];
    }, [campaign.adGroups]);

    const { data: initialDspAudienceSources } = useQuery({
        queryKey: ['campaign/wizard/dspAudiences/initial', adGroupAudienceSources],
        queryFn: async () => {
            const { data } = await ApiService.getDspAudienceSources({
                ids: adGroupAudienceSources?.map(({ dspAudienceId }) => dspAudienceId),
            });

            return data;
        },
        enabled: !!adGroupAudienceSources.length,
        refetchOnWindowFocus: false,
    });

    const { data: defaultDspAudienceSources } = useQuery({
        queryKey: ['campaign/wizard/dspAudiences/default', defaultTargetAudienceIds],
        queryFn: async () => {
            const { data } = await ApiService.getDspAudienceSources({
                ids: defaultTargetAudienceIds,
            });

            return data;
        },
        enabled: !!defaultTargetAudienceIds.length,
        refetchOnWindowFocus: false,
    });

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.name) {
            props.onChange({
                [event.target.name]: event.target.value,
            });
        }
    };

    useEffect(() => {
        if ($loader) {
            if (isFetchingSelections) {
                $loader.loading('fetchAudienceSources');
            } else {
                $loader.loaded('fetchAudienceSources');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFetchingSelections]);

    useEffect(() => {
        if (campaign?.adGroups?.length) {
            if (initialDspAudienceSources?.length) {
                setAudienceSources(
                    initialDspAudienceSources?.map((dspAudience: DspAudience) => toDspAudienceDTO(dspAudience)) || []
                );
            } else {
                assignDefaultAudienceSources();
            }
        } else {
            setAudienceSources([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaign.adGroups, defaultDspAudienceSources, initialDspAudienceSources]);

    const updateAudienceSources = useCallback(
        (newAudienceSources: AudienceSource[]) => {
            setAudienceSources(newAudienceSources);

            if (onChange) {
                if (campaign?.adGroups?.length) {
                    campaign.adGroups[0] = {
                        ...campaign.adGroups[0],
                        adGroupAudienceSources: toCampaignAdGroupAudienceSources(
                            newAudienceSources,
                            campaign.adGroups[0].adGroupAudienceSources ?? []
                        ),
                    };
                } else {
                    campaign.adGroups.push({
                        adGroupAudienceSources: toCampaignAdGroupAudienceSources(newAudienceSources),
                    });
                }

                onChange({
                    adGroups: campaign.adGroups,
                });
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        },
        [onChange, campaign]
    );

    const assignDefaultAudienceSources = useCallback(() => {
        updateAudienceSources(
            defaultDspAudienceSources?.map((dspAudience: DspAudience) => toDspAudienceDTO(dspAudience)) || []
        );
    }, [defaultDspAudienceSources, updateAudienceSources]);

    const handleAudienceSourceChange = (newAudienceSources: AudienceSource[]): void => {
        updateAudienceSources(newAudienceSources);
    };

    return (
        <Card id="campaign-wizard-targeting-audience" variant="outlined">
            <CardHeader
                title={
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="body1" color="text.primary">
                            Audience
                        </Typography>
                    </Stack>
                }
            />
            <CardContent>
                <Row>
                    <FormControl fullWidth error={hasError('audienceType')}>
                        <Column gap={3}>
                            <FormLabel required={true}>Audience Categories and Segments</FormLabel>

                            <CampaignAudienceTypeRadioGroup
                                name="audienceType"
                                value={campaign.audienceType ?? ''}
                                onChange={handleChange}
                                disabled={!(isDraftCampaign || isNewCampaign)}
                            />

                            {hasError('audienceType') && (
                                <FormHelperText error={true}>
                                    Campaign Audience Type selection is required
                                </FormHelperText>
                            )}

                            <Box>
                                <AudienceSourceField
                                    mediaType={campaign.mediaType}
                                    selections={selections}
                                    fetchSelections={fetchSelections}
                                    onChange={handleAudienceSourceChange}
                                    items={audienceSources}
                                    slotProps={{
                                        button: {
                                            disabled:
                                                campaign.audienceType === 'AI' || !(isDraftCampaign || isNewCampaign),
                                        },
                                    }}
                                />
                            </Box>

                            {campaign.audienceType === 'CUSTOM' && (
                                <>
                                    {hasError('adGroups.*.adGroupAudienceSources') && (
                                        <FormHelperText error={true} sx={{ ml: 7 }}>
                                            Custom Audience(s) creation is required. Please add at least one audience.
                                        </FormHelperText>
                                    )}
                                </>
                            )}
                        </Column>
                    </FormControl>
                </Row>
            </CardContent>
        </Card>
    );
}
